/**
 * styles.ts — all embed.js CSS in one place.
 *
 * Injected as a single <style> tag on first use.
 * Prefixed with .mp- to avoid collisions with host page styles.
 * Uses CSS custom properties so color can be overridden per-instance.
 */

export const EMBED_CSS = `
/* ── Reset ──────────────────────────────────────────────────────────────────── */
.mp-embed-iframe {
  display: block;
  width: 100%;
  height: 820px;
  border: none;
  transition: height 120ms ease;
  border-radius: 12px;
  background: #f8fafc;
}

/* ── Inline wrapper ─────────────────────────────────────────────────────────── */
.mp-inline-wrap {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
}

/* ── Popup backdrop ─────────────────────────────────────────────────────────── */
.mp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2147483646; /* one below modal */
  opacity: 0;
  transition: opacity 180ms ease;
  cursor: pointer;
}
.mp-backdrop.mp-visible {
  opacity: 1;
}

/* ── Popup modal ────────────────────────────────────────────────────────────── */
.mp-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -48%);
  z-index: 2147483647;
  width: min(640px, calc(100vw - 32px));
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,0.28);
  opacity: 0;
  transition: opacity 180ms ease, transform 180ms ease;
  background: #f8fafc;
}
.mp-modal.mp-visible {
  opacity: 1;
  transform: translate(-50%, -50%);
}
.mp-modal .mp-embed-iframe {
  border-radius: 0;
  height: 100%;
}
/* Fit the viewport: the booking page scrolls internally in modal mode. */
.mp-modal {
  height: min(820px, calc(100vh - 32px));
  height: min(820px, calc(100dvh - 32px));
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
}
/* Phones: full-screen sheet so nothing is cut off top or bottom. */
@media (max-width: 640px) {
  .mp-modal,
  .mp-modal.mp-visible {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
  }
}

/* ── Popup close button ─────────────────────────────────────────────────────── */
.mp-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0,0,0,0.45);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  padding: 0;
  transition: background 120ms ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.mp-close-btn:hover,
.mp-close-btn:focus-visible {
  background: rgba(0,0,0,0.70);
  outline: 2px solid #fff;
  outline-offset: 1px;
}

/* ── Floating action button ─────────────────────────────────────────────────── */
.mp-fab {
  position: fixed;
  bottom: 24px;
  z-index: 2147483645;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
  background: var(--mp-fab-color, #0ea5e9);
  box-shadow: 0 4px 16px rgba(0,0,0,0.22);
  transition: box-shadow 150ms ease, transform 150ms ease;
  white-space: nowrap;
}
.mp-fab:hover,
.mp-fab:focus-visible {
  box-shadow: 0 6px 24px rgba(0,0,0,0.30);
  transform: translateY(-1px);
  outline: none;
}
.mp-fab:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 2px;
}
.mp-fab.mp-fab-right { right: 24px; }
.mp-fab.mp-fab-left  { left:  24px; }

/* ── Body scroll lock (applied to <body> when popup is open) ───────────────── */
body.mp-scroll-locked {
  overflow: hidden !important;
}

/* ── Reduced motion ─────────────────────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .mp-backdrop,
  .mp-modal,
  .mp-fab {
    transition: none !important;
  }
}
`.trim()

let injected = false

export function injectStyles(): void {
  if (injected) return
  injected = true
  const style = document.createElement('style')
  style.id = 'mp-embed-styles'
  style.textContent = EMBED_CSS
  document.head.appendChild(style)
}
